import React from "react";
import { Box, IconButton, Typography, useTheme } from "@mui/material";
import { SiteTrafficPanelProps } from "./SiteTrafficPanel.types";
import { useSiteTrafficPanel } from "./SiteTrafficPanel.hooks";
import { ComponentLoader, InfoTooltip, Panel } from "../../components";
import { Decrease, Increase, InfoOutline } from "@tiny/icons";
import TimeSeriesChart from "../../components/TimeSeriesChart/TimeSeriesChart";
import { formatNumber } from "../../utils/formatUtils";

function SiteTrafficPanel(props: SiteTrafficPanelProps) {
  const theme = useTheme();

  const {
    statistics,
    xValueAccessor,
    yValueAccessor,
    isLoadingGroupedStatistics,
    groupTimeUnit,
    lastPeriod,
  } = useSiteTrafficPanel(props);

  return (
    <Panel
      title="Increase in Site Traffic"
      showDivider
      actions={
        <InfoTooltip
          message="Shows how many users clicked links in your emails, reflecting content engagement."
          variant="white"
          noInfoIcon
        >
          <IconButton sx={{ p: 0, ml: 1 }}>
            <InfoOutline />
          </IconButton>
        </InfoTooltip>
      }
    >
      <Box sx={{ height: 280, position: "relative" }}>
        {isLoadingGroupedStatistics ? (
          <ComponentLoader height="100%" />
        ) : (
          <>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mt: 4,
                px: 5,
              }}
            >
              <Box>
                <Typography variant="label1R" color="grey.80">
                  Email Clicks
                </Typography>

                <Typography variant="h1" fontWeight="bold" color="grey.100">
                  {formatNumber(statistics?.currentTotal) || "0"}
                </Typography>

                <Box sx={{ display: "flex", alignItems: "baseline", mt: 0.5 }}>
                  {statistics?.comparePercent === null ||
                  statistics?.comparePercent === undefined ? (
                    <>
                      <Typography variant="caption" color="grey.80">
                        - 0.0%
                      </Typography>
                    </>
                  ) : statistics.comparePercent >= 0 ? (
                    <>
                      <Increase
                        sx={{
                          color: theme.palette.green.dark,
                          fontSize: "10px !important",
                          mr: 0.5,
                        }}
                      />
                      <Typography variant="caption" color="green.dark">
                        {Math.abs(statistics?.comparePercent).toFixed(1)}%
                      </Typography>
                    </>
                  ) : (
                    <>
                      <Decrease
                        sx={{
                          color: theme.palette.red.main,
                          fontSize: "10px !important",
                          mr: 0.5,
                        }}
                      />
                      <Typography variant="caption" color="red.main">
                        {Math.abs(statistics?.comparePercent).toFixed(1)}%
                      </Typography>
                    </>
                  )}
                  <Typography variant="caption" color="grey.80">
                    &nbsp;vs last {lastPeriod}
                  </Typography>
                </Box>
              </Box>
              <Box
                component="img"
                src="https://tinyemail-stripo.s3.ca-central-1.amazonaws.com/static/dashboard/screen.svg"
                alt="Email statistics icon"
                sx={{ width: 64, height: 64 }}
              />
            </Box>
            <Box p={1}>
              <TimeSeriesChart
                data={statistics?.statistics || []}
                xAccessor={xValueAccessor}
                yAccessor={yValueAccessor}
                height={200}
                numYTicks={6}
                margin={{ top: 20, right: 15, bottom: 40, left: 15 }}
              />
            </Box>
          </>
        )}
      </Box>
    </Panel>
  );
}

export default SiteTrafficPanel;
